import React, { useState } from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_SIZE_SMALL, BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Icon from '@divvy-web/skylab.icon'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import NewCollaborator from '@sputnik/components/InviteCollaborators/Collaborator/NewCollaborator'
import CollaboratorForm from '@sputnik/components/InviteCollaborators/CollaboratorForm'
import { useInviteCollaborator } from '@sputnik/components/InviteCollaborators/CollaboratorForm/InviteCollaborator.gql'
import { useUpdateCollaborator } from '@sputnik/components/InviteCollaborators/CollaboratorForm/UpdateCollaborator.gql'
import {
  GetApplicationEmails,
  GetApplicationEmailsDocument,
  GetApplicationEmailsVariables,
} from '@sputnik/hooks/useApplicationCollaborators/GetApplicationEmails.gql'
import useNewApplicationCollaborators from '@sputnik/hooks/useApplicationCollaborators/useNewApplicationCollaborators'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'

interface CollaboratorsListProps {
  appId: string
  onInviteCollaborator?: () => void
  onDeleteCollaborator?: () => void
}

const CollaboratorsList = ({ appId, onInviteCollaborator, onDeleteCollaborator }: CollaboratorsListProps) => {
  const [getClassName] = useNameFormatter('CollaboratorsList')
  const showToast = useToast()

  const { collaborators } = useNewApplicationCollaborators({ salesforceCreditId: appId ?? '' })

  const [activeCollaboratorId, setActiveCollaboratorId] = useState<string | null>(null)

  const isEditing = activeCollaboratorId && activeCollaboratorId !== 'new'

  const [inviteCollaborator, { client }] = useInviteCollaborator({
    onCompleted: (data) => {
      logInfo('inviteCollaborators', {
        attributes: { creditAppId: appId, result: 'Collaborator invited' },
        eventName: 'inviteCollaboratorsSuccess',
      })
      onInviteCollaborator?.()
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Invites successfully sent'
          id='sputnik.CollaboratorsList__ixk27n'
        />,
      )
      setActiveCollaboratorId(null)

      client.cache.updateQuery<GetApplicationEmails, GetApplicationEmailsVariables>(
        {
          query: GetApplicationEmailsDocument,
          variables: { creditApplicationId: appId },
        },
        (cacheData) => {
          if (!cacheData?.creditApplication) return cacheData

          const newCollaborators = (data.inviteCollaborator ?? []).map((c) => ({
            ...c,
            lastLoginAt: null,
          }))
          const existing = cacheData.creditApplication.collaborators ?? []
          const existingIds = new Set(existing.map((c) => c?.id))
          const toAdd = newCollaborators.filter((c) => c.id && !existingIds.has(c.id))

          return {
            ...cacheData,
            creditApplication: {
              ...cacheData.creditApplication,
              collaborators: [...existing, ...toAdd],
            },
          }
        },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'inviteCollaborators',
          creditAppId: appId,
          message: error?.message,
          result: 'failed to invite collaborator',
        },
        eventName: 'inviteCollaboratorsFailure',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to invite collaborators'
          id='sputnik.CollaboratorsList__011Ad8'
        />,
      )
    },
  })

  const [updateCollaborator] = useUpdateCollaborator({
    onCompleted: (data) => {
      logInfo('updateCollaborator', {
        attributes: { creditAppId: appId, result: 'collaborator updated' },
        eventName: 'updateCollaboratorSuccess',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Collaborator successfully updated'
          id='sputnik.CollaboratorList__hYlq8P'
        />,
      )
      setActiveCollaboratorId(null)

      client.cache.updateQuery<GetApplicationEmails, GetApplicationEmailsVariables>(
        {
          query: GetApplicationEmailsDocument,
          variables: { creditApplicationId: appId },
        },
        (cacheData) => {
          if (!cacheData?.creditApplication) return cacheData

          const updatedMap = new Map(
            (data.inviteCollaborator ?? []).map((collaborator) => [collaborator.id, collaborator]),
          )
          const existing = cacheData.creditApplication.collaborators ?? []

          return {
            ...cacheData,
            creditApplication: {
              ...cacheData.creditApplication,
              collaborators: existing.map((collaborator) =>
                collaborator?.id && updatedMap.has(collaborator.id)
                  ? { ...collaborator, ...updatedMap.get(collaborator.id) }
                  : collaborator,
              ),
            },
          }
        },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'updateCollaborator',
          creditAppId: appId,
          message: error?.message,
          result: 'failed to update collaborator',
        },
        eventName: 'updateCollaboratorFailure',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to update collaborator'
          id='sputnik.CollaboratorForm__3kRmNq'
        />,
      )
    },
  })

  return (
    <div className={styles[getClassName('')]}>
      {collaborators?.length > 0 && (
        <div className={styles[getClassName('collaborators')]}>
          {collaborators.map((collaborator) => (
            <div key={collaborator.id}>
              {isEditing && activeCollaboratorId === collaborator.id ? (
                <CollaboratorForm
                  allCollaborators={collaborators}
                  appId={appId}
                  collaborator={collaborator}
                  setActiveCollaboratorId={setActiveCollaboratorId}
                  onSubmit={updateCollaborator}
                />
              ) : (
                <span className='dd-privacy-mask'>
                  <NewCollaborator
                    appId={appId}
                    collaborator={collaborator}
                    handleEditCollaborator={() => setActiveCollaboratorId?.(collaborator.id as string)}
                    onDeleteCollaborator={() => {
                      onDeleteCollaborator?.()
                    }}
                  />
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {(activeCollaboratorId === 'new' || collaborators?.length < 1) && (
        <div className={styles[getClassName('new-invite-form')]}>
          <CollaboratorForm
            allCollaborators={collaborators ?? []}
            appId={appId}
            setActiveCollaboratorId={setActiveCollaboratorId}
            onSubmit={inviteCollaborator}
          />
        </div>
      )}

      {!activeCollaboratorId && collaborators?.length > 0 && (
        <div className={styles[getClassName('add-container')]}>
          <BasicButton
            className={styles[getClassName('add-button')]}
            size={BASIC_BUTTON_SIZE_SMALL}
            type={BASIC_BUTTON_TYPE_FLAT}
            onClick={() => {
              setActiveCollaboratorId('new')
            }}
          >
            <FormattedMessage
              defaultMessage='Add collaborator'
              id='sputnik.CollaboratorsList__0K9j9i'
            />
            <Icon name='plus' />
          </BasicButton>
        </div>
      )}
    </div>
  )
}

export default CollaboratorsList
